import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { setHeaderCollapse } from "../../../../core/data/redux/commonSlice";
import { AuthContext } from "../../../../context/authcontext";
import { all_routes } from "../../../router/all_routes";
import { HeaderAction, ExportDropdown, ExportKind } from "../common";
import { ChartMetric, GranularityToggle, MetricToggle } from "../chartControls";
import DashboardFilterDropdown, {
  ActiveFilterChips,
  useDashboardFilters,
  hasFilterChips,
  describeFilters,
  toApiFilters,
} from "../filters/DashboardFilterDropdown";
import { downloadExportFile } from "../dashboardApi";
import { useMoneyFormat } from "../../../../components/currencyFormat";
import { getLifecycleLabels } from "../../../../utils/lifecycleUtils";
import { exportDashboardPdf } from "../exportDashboardPdf";
import { fetchDashboardStats, exportDashboardStatsExcel, DashboardStats, AnalyticsPeriod } from "./dashboardService";
import {
  SkeletonBlock,
  RevenueChartSkeleton,
  TrafficDonutSkeleton,
  TrafficListSkeleton,
  KpiCardSkeleton,
  TopDealsSkeleton,
  PipelineSkeleton,
  DealsOverviewSkeleton,
  RecentDealsSkeletonRows,
} from "./DashboardSkeletons";
import {
  palette,
  pipelineStageColors,
  dealOutcomes,
  TOP_DEALS_LIMIT,
  trafficColors,
  trafficOthersColor,
  scorecardMeta,
  defaultScorecardMeta,
  currencyScorecards,
  dealSegments,
  tagClassName,
  statusClassName,
} from "./constants";
import {
  initialsOf,
  PersonAvatar,
  MoneyBagIcon,
  PDF_CAPTURE_CSS,
  SectionTitle,
  EmptyState,
} from "./DashboardParts";
import "./newDashboard.css";

const route = all_routes;

const formatNumber = (value: number) => (Number.isFinite(value) ? value.toLocaleString() : "0");

const parseGrowth = (growth?: string | number | null) => {
  const parsed = parseFloat(String(growth ?? ""));
  return Number.isFinite(parsed) ? parsed : null;
};

const isNegativeGrowth = (growth?: string | number | null) => (parseGrowth(growth) ?? 0) < 0;

const formatGrowth = (growth?: string | number | null) => {
  const text = String(growth ?? "").trim();
  if (!text) return "0%";
  if (/^[+-]/.test(text)) return text;
  return (parseGrowth(text) ?? 0) > 0 ? `+${text}` : text;
};

const growthBadgeClass = (growth?: string | number | null) =>
  isNegativeGrowth(growth) ? "badge-soft-danger" : "badge-soft-success";

const formatChartDate = (date: string, period: AnalyticsPeriod) => {
  if (period === "by_year") return date;
  const parsed = dayjs(date);
  if (!parsed.isValid()) return date;
  return parsed.format(period === "by_week" ? "DD MMM YYYY" : "MMM YYYY");
};

const niceMax = (value: number, ticks: number) => {
  if (!(value > 0)) return ticks;
  const rawStep = value / ticks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  return Math.ceil(rawStep / magnitude) * magnitude * ticks;
};

const CompanyLogo = ({ name, url }: { name: string; url: string | null }) => {
  const [failed, setFailed] = useState(false);
  return (
    <span className="avatar avatar-md border rounded-circle flex-shrink-0 top-deal-logo bg-white">
      {url && !failed ? (
        <img src={url} alt={name} onError={() => setFailed(true)} />
      ) : (
        <span className="fw-semibold text-dark fs-13">{initialsOf(name)}</span>
      )}
    </span>
  );
};

const sparklineXAxis: ApexXAxis = {
  labels: { show: false },
  axisBorder: { show: false },
  axisTicks: { show: false },
  tooltip: { enabled: false },
};

const sparklineGrid: ApexGrid = {
  show: false,
  padding: { top: 0, right: 0, bottom: 0, left: 0 },
};

const DEAL_NAME_MAX_LENGTH = 40;

const TruncatedLink = ({ to, text, className }: { to: string; text: string; className?: string }) => {
  const link = (
    <Link to={to} className={className}>
      {text.length > DEAL_NAME_MAX_LENGTH ? `${text.slice(0, DEAL_NAME_MAX_LENGTH)}...` : text}
    </Link>
  );
  if (text.length <= DEAL_NAME_MAX_LENGTH) return link;
  return (
    <OverlayTrigger placement="top" overlay={<Tooltip id={`deal-name-${to}`}>{text}</Tooltip>}>
      {link}
    </OverlayTrigger>
  );
};

const hideRevenueGridLines = (container: HTMLElement) => {
  container
    .querySelectorAll(
      ".nd-revenue-chart .apexcharts-gridlines-horizontal line, .nd-revenue-chart .apexcharts-grid-borders line:not([x1='0'])"
    )
    .forEach((line) => line.setAttribute("stroke", "transparent"));
};

const NewDashboard = () => {
  const dispatch = useDispatch();
  const headerCollapse = useSelector((state: any) => state.commonSlice.headerCollapse);
  const { selectedTenantId, lifecycleTypeNames } = useContext(AuthContext);
  const names = useMemo(() => getLifecycleLabels(lifecycleTypeNames), [lifecycleTypeNames]);

  const [data, setData] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const { filters, setFilters, defaults: defaultFilters } = useDashboardFilters();
  const [exporting, setExporting] = useState<ExportKind | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const [revenuePeriod, setRevenuePeriod] = useState<AnalyticsPeriod>("by_week");
  const [trafficMetric, setTrafficMetric] = useState<ChartMetric>("count");

  const apiFilters = useMemo(() => toApiFilters(filters), [filters]);
  const money = useMoneyFormat(filters.currencyCode);
  const formatCurrency = money.compact;

  useEffect(() => {
    setData(null);
  }, [selectedTenantId]);

  useEffect(() => {
    if (!selectedTenantId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDashboardStats(selectedTenantId, apiFilters)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Failed to load dashboard");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTenantId, reloadKey, apiFilters]);

  const reload = () => setReloadKey((key) => key + 1);

  const loaded = loading ? null : data;

  const revenuePoints = useMemo(
    () => data?.revenueAnalytics[revenuePeriod] ?? [],
    [data, revenuePeriod]
  );

  const revenueSeries = useMemo(
    () => [
      { name: "Revenue", type: "column", data: revenuePoints.map((point) => point.revenue) },
      { name: "Sales", type: "area", data: revenuePoints.map((point) => point.sales) },
    ],
    [revenuePoints]
  );

  const revenueOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        type: "line",
        toolbar: { show: false },
        zoom: { enabled: true, type: "x", allowMouseWheelZoom: true },
        foreColor: palette.axis,
      },
      colors: [palette.redLight, palette.area],
      plotOptions: { bar: { columnWidth: "60%", borderRadius: 0 } },
      fill: {
        type: ["gradient", "solid"],
        opacity: [1, 1],
        gradient: {
          type: "vertical",
          shadeIntensity: 0,
          gradientToColors: [palette.red, palette.area],
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100],
        },
      },
      stroke: { width: [0, 0], curve: "smooth" },
      markers: { size: 0 },
      dataLabels: { enabled: false },
      legend: { show: false },
      grid: {
        borderColor: palette.grid,
        strokeDashArray: 5,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: true } },
        padding: { left: 4, right: 0 },
      },
      xaxis: {
        categories: revenuePoints.map((point) => formatChartDate(point.date, revenuePeriod)),
        tickPlacement: "on",
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        tooltip: { enabled: true },
      },
      yaxis: {
        min: 0,
        max: niceMax(Math.max(0, ...revenuePoints.map((point) => Math.max(point.revenue, point.sales))), 6),
        tickAmount: 6,
        labels: { formatter: (val: number) => formatCurrency(val), style: { fontSize: "11px" } },
      },
      tooltip: {
        shared: true,
        intersect: false,
        marker: { show: false },
        y: { formatter: (val: number) => formatCurrency(val) },
      },
    }),
    [revenuePoints, revenuePeriod, formatCurrency]
  );

  const traffic = useMemo(() => {
    const metricOf = (source: { count: number; amount: number }) =>
      trafficMetric === "count" ? source.count : source.amount;
    const sorted = [...(data?.trafficSources ?? [])].sort((a, b) => metricOf(b) - metricOf(a));
    const top = sorted.slice(0, trafficColors.length).map((source, index) => ({
      label: source.source,
      count: source.count,
      amount: source.amount,
      value: metricOf(source),
      color: trafficColors[index],
    }));
    const rest = sorted.slice(trafficColors.length);
    const restCount = rest.reduce((sum, source) => sum + source.count, 0);
    const restAmount = rest.reduce((sum, source) => sum + source.amount, 0);
    const restValue = trafficMetric === "count" ? restCount : restAmount;
    if (restValue > 0) {
      top.push({
        label: "Others",
        count: restCount,
        amount: restAmount,
        value: restValue,
        color: trafficOthersColor,
      });
    }
    return top;
  }, [data, trafficMetric]);
  const trafficTotal = traffic.reduce((sum, source) => sum + source.value, 0);
  const formatTrafficValue = (value: number) =>
    trafficMetric === "count" ? formatNumber(value) : formatCurrency(value);
  const trafficShare = (value: number) => `${trafficTotal > 0 ? Math.round((value / trafficTotal) * 100) : 0}%`;

  const trafficOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "donut" },
      labels: traffic.map((source) => source.label),
      colors: traffic.map((source) => source.color),
      legend: { show: false },
      stroke: { width: 4, colors: ["#fff"] },
      dataLabels: {
        enabled: true,
        formatter: (val: number) => `${Math.round(val)}%`,
        style: { fontSize: "11px", fontWeight: 700, colors: ["#fff"] },
        dropShadow: { enabled: false },
      },
      plotOptions: {
        pie: {
          expandOnClick: false,
          dataLabels: { offset: 0, minAngleToShowLabel: 12 },
          donut: { size: "62%", labels: { show: false } },
        },
      },
      states: { active: { filter: { type: "none" } } },
      tooltip: {
        y: {
          formatter: (_val: number, opts?: any) => {
            const source = traffic[opts?.seriesIndex ?? 0];
            return source
              ? `${formatNumber(source.count)} ${names.deals} · ${formatCurrency(source.amount)}`
              : "";
          },
        },
      },
    }),
    [traffic, formatCurrency, names.deals]
  );

  const trafficSeries = useMemo(() => traffic.map((source) => source.value), [traffic]);

  const scorecards = data?.scorecards ?? [];
  const kpiCards = scorecards.filter((card) => card.key !== "total_contacts").slice(0, 3);
  const contactsCard = scorecards.find((card) => card.key === "total_contacts");
  const latestContacts = contactsCard?.latest_contacts ?? [];
  const visibleContacts = latestContacts.slice(0, 3);
  const hiddenContactCount = latestContacts.length - visibleContacts.length;

  const topDeals = (data?.topDeals ?? []).slice(0, TOP_DEALS_LIMIT);

  const pipelineStages = useMemo(
    () =>
      data
        ? dealOutcomes.map((outcome) => ({
          key: outcome.key,
          display_name: outcome.label,
          revenue: data.dealStatsByAmount[outcome.key].amount,
          count: data.dealStatsByAmount[outcome.key].count,
        }))
        : [],
    [data]
  );
  const hasPipelineData = pipelineStages.some((stage) => stage.count > 0 || stage.revenue > 0);
  const pipelineBasis = pipelineStages.some((stage) => stage.revenue > 0)
    ? pipelineStages.map((stage) => stage.revenue)
    : pipelineStages.map((stage) => stage.count);
  const pipelineMax = Math.max(0, ...pipelineBasis);
  const pipelineColumnClass = `col-${Math.max(3, Math.floor(12 / Math.max(1, pipelineStages.length)))}`;

  const pipelineSeries = useMemo(
    () => [
      {
        name: names.pipeline,
        data: pipelineBasis.map((value) => (pipelineMax > 0 ? Math.max(8, (value / pipelineMax) * 100) : 8)),
      },
    ],
    [pipelineBasis, pipelineMax, names.pipeline]
  );

  const pipelineChartOptions = useMemo<ApexOptions>(
    () => ({
      chart: { type: "bar", sparkline: { enabled: true } },
      colors: pipelineStages.map((stage) => pipelineStageColors[stage.key]),
      plotOptions: { bar: { columnWidth: "90%", distributed: true, borderRadius: 0 } },
      states: { active: { filter: { type: "none" } } },
      xaxis: { ...sparklineXAxis, categories: pipelineStages.map((stage) => stage.display_name) },
      yaxis: { show: false, min: 0, max: 100 },
      grid: { ...sparklineGrid },
      tooltip: {
        x: { show: true },
        marker: { show: true },
        y: {
          title: { formatter: () => "" },
          formatter: (_val: number, opts?: any) => {
            const stage = pipelineStages[opts?.dataPointIndex ?? 0];
            return stage ? `${formatCurrency(stage.revenue)} · ${stage.count} ${names.deals}` : "";
          },
        },
      },
    }),
    [pipelineStages, formatCurrency, names.deals]
  );

  const dealsOverview = data?.dealStatsByCount;
  const dealSegmentTotal = dealSegments.reduce((sum, segment) => sum + (dealsOverview?.[segment.key] ?? 0), 0);
  const dealsGrowth = dealsOverview?.growth ?? "";

  const recentDeals = data?.recentDeals ?? [];

  const handleExport = async (kind: ExportKind) => {
    const container = reportRef.current;
    if (!selectedTenantId || !data || exporting) return;
    setExporting(kind);
    try {
      if (kind === "pdf") {
        if (!container) return;
        await new Promise((resolve) => setTimeout(resolve, 150));
        await exportDashboardPdf({
          container,
          sectionSelector: ".nd-section",
          title: "Dashboard",
          filtersText: describeFilters(filters, true),
          fileName: `dashboard-${dayjs().format("YYYY-MM-DD")}.pdf`,
          captureCss: PDF_CAPTURE_CSS,
          onClone: hideRevenueGridLines,
        });
      } else {
        const file = await exportDashboardStatsExcel(selectedTenantId, apiFilters);
        await downloadExportFile(file, `dashboard-${dayjs().format("YYYY-MM-DD")}`);
      }
    } catch (err) {
      setError((err as Error)?.message || `Failed to export ${kind === "pdf" ? "PDF" : "Excel"}`);
    } finally {
      setExporting(null);
    }
  };

  const header = (
    <>
      <div
        className={`d-flex align-items-center justify-content-between gap-2 flex-wrap ${hasFilterChips(filters, defaultFilters, true) ? "mb-3" : "mb-4"
          }`}
      >
        <div>
          <h4 className="mb-0">Dashboard</h4>
        </div>
        <div className="gap-2 d-flex align-items-center flex-wrap">
          {selectedTenantId && (
            <DashboardFilterDropdown
              title="Dashboard Filters"
              filters={filters}
              onChange={setFilters}
              loading={loading}
              showDefaultDateRange
            />
          )}
          <ExportDropdown
            disabled={!data || loading}
            exporting={exporting}
            spinClassName="nd-spin"
            onExport={handleExport}
          />
          <HeaderAction label="Refresh" icon={`ti ti-refresh${loading ? " nd-spin" : ""}`} onClick={reload} />
          <HeaderAction
            label={headerCollapse ? "Expand" : "Collapse"}
            icon="ti ti-transition-top"
            onClick={() => dispatch(setHeaderCollapse(!headerCollapse))}
          />
        </div>
      </div>
      <ActiveFilterChips filters={filters} onChange={setFilters} disabled={loading} showDefaultDateRange />
      {loading && (
        <span className="visually-hidden" role="status">
          Loading dashboard
        </span>
      )}
    </>
  );

  if (!selectedTenantId) {
    return (
      <div className="page-wrapper new-dashboard">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-building-community fs-1 text-muted mb-3" />
              <h5>Select a Tenant</h5>
              <p className="text-muted mb-0">Please select a tenant to view the dashboard.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data && error && !loading) {
    return (
      <div className="page-wrapper new-dashboard">
        <div className="content pb-0">
          {header}
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="ti ti-alert-circle fs-1 text-danger mb-3" />
              <h5>Unable to load dashboard</h5>
              <p className="text-muted mb-3">{error}</p>
              <button type="button" className="btn btn-outline-light border" onClick={reload}>
                <i className="ti ti-refresh me-1" />
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper new-dashboard">
      <div className="content pb-0">
        {header}

        {error && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between gap-2 mb-4">
            <span>{error}</span>
            <button type="button" className="btn btn-sm btn-outline-light" onClick={reload}>
              Try Again
            </button>
          </div>
        )}

        <div ref={reportRef} aria-busy={!loaded}>
          <div className="row nd-section">
            <div className="col-xxl-8 col-xl-7 d-flex">
              <div className="card flex-fill">
                <div className="card-body pb-0">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title="Revenue Analytics" />
                    <GranularityToggle value={revenuePeriod} onChange={setRevenuePeriod} />
                  </div>
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      {loaded ? (
                        <h4 className="mb-0">{formatCurrency(loaded.revenueAnalytics.total_revenue)}</h4>
                      ) : (
                        <SkeletonBlock width={110} height={26} />
                      )}
                      <p className="mb-0">Revenue with Sales ({money.code})</p>
                    </div>
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <div className="d-flex align-items-center border rounded px-2 py-1">
                        <p className="d-flex align-items-center mb-0">
                          <i className="ti ti-circle-filled fs-8 nd-red me-1" />
                          Revenue
                        </p>
                      </div>
                      <div className="d-flex align-items-center border rounded px-2 py-1">
                        <p className="d-flex align-items-center mb-0">
                          <i className="ti ti-circle-filled fs-8 nd-muted-dot me-1" />
                          Sales
                        </p>
                      </div>
                    </div>
                  </div>
                  {!loaded ? (
                    <RevenueChartSkeleton />
                  ) : revenuePoints.length ? (
                    <div className="nd-revenue-chart">
                      <Chart options={revenueOptions} series={revenueSeries} type="line" height={340} />
                    </div>
                  ) : (
                    <EmptyState message="No revenue data for this period" />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-xl-5 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-0">
                    <SectionTitle title={`${names.deal} by Traffic Sources`} />
                    <div className="d-flex align-items-center gap-2">
                      <MetricToggle value={trafficMetric} onChange={setTrafficMetric} />
                      <Link to={route.deals} className="btn btn-sm btn-icon btn-outline-light border">
                        <i className="ti ti-arrow-right" />
                      </Link>
                    </div>
                  </div>
                  {!loaded ? (
                    <TrafficDonutSkeleton />
                  ) : trafficTotal > 0 ? (
                    <Chart
                      key={trafficMetric}
                      options={trafficOptions}
                      series={trafficSeries}
                      type="donut"
                      height={270}
                    />
                  ) : (
                    <EmptyState
                      message={trafficMetric === "count" ? "No traffic recorded yet" : "No traffic value recorded yet"}
                      compact
                    />
                  )}
                </div>
                {!loaded ? (
                  <TrafficListSkeleton />
                ) : (
                  <div className="mb-1">
                    {traffic.map((source, index) => (
                      <div
                        key={source.label}
                        className={`px-3 d-flex align-items-center justify-content-between ${index === traffic.length - 1 ? "pt-2 pb-3" : "py-2 border-bottom"
                          }`}
                      >
                        <p className="text-dark d-flex align-items-center mb-0 text-truncate" title={source.label}>
                          <i className="ti ti-circle-filled fs-8 me-1" style={{ color: source.color }} />
                          {source.label}
                        </p>
                        <p className="text-dark mb-0 ms-2 text-nowrap">
                          <span className="fw-semibold">{formatTrafficValue(source.value)}</span>
                          <span className="nd-traffic-share ms-2">{trafficShare(source.value)}</span>
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="row nd-section">
            {!loaded &&
              Array.from({ length: 4 }, (_, index) => (
                <div className="col-xl-3 col-sm-6 d-flex" key={index}>
                  <KpiCardSkeleton />
                </div>
              ))}
            {loaded && kpiCards.map((card) => {
              const meta = scorecardMeta[card.key] ?? defaultScorecardMeta;
              const byValue = currencyScorecards.includes(card.key);
              const label = meta.label ? meta.label(names) : card.display_name;
              return (
                <div className="col-xl-3 col-sm-6 d-flex" key={card.key}>
                  <div className="kpi-card-wrap flex-fill">
                    <div className="card kpi-card h-100 mb-0">
                      <div className="card-body">
                        <p className="fw-medium mb-1 d-flex align-items-center gap-2 nd-kpi-label">
                          <span className="text-truncate">{label}</span>

                        </p>
                        <h4 className="mb-3 d-flex align-items-baseline gap-2 flex-wrap">
                          {byValue ? formatCurrency(card.amount ?? 0) : formatNumber(card.count ?? 0)}
                          {!byValue && card.amount !== null && (
                            <span className="nd-kpi-worth">worth {formatCurrency(card.amount)}</span>
                          )}
                        </h4>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span
                            className={`d-inline-flex align-items-center badge rounded-pill ${growthBadgeClass(
                              card.growth
                            )} border-0`}
                            title={
                              byValue
                                ? `Change in ${label.toLowerCase()} value compared with the previous period`
                                : `Change in the number of ${label.toLowerCase()} compared with the previous period`
                            }
                          >
                            {formatGrowth(card.growth)}
                          </span>
                          <p className="text-dark mb-0">{"vs Last Period"}</p>
                        </div>
                      </div>
                    </div>
                    <span
                      className="kpi-icon"
                      style={{ background: `linear-gradient(180deg, ${meta.gradient[0]}, ${meta.gradient[1]})` }}
                    >
                      {meta.icon === "moneybag" ? (
                        <MoneyBagIcon color={meta.gradient[0]} symbol={money.symbol} />
                      ) : (
                        <i className={meta.icon} />
                      )}
                    </span>
                  </div>
                </div>
              );
            })}

            {loaded && contactsCard && (
              <div className="col-xl-3 col-sm-6 d-flex">
                <div className="card flex-fill">
                  <div className="card-body position-relative">
                    <div className="mb-3">
                      <div className="d-flex align-items-center gap-1">
                        <h4 className="mb-0">{formatNumber(contactsCard.count ?? 0)}</h4>
                        <span
                          className={`d-inline-flex align-items-center badge rounded-pill ${growthBadgeClass(
                            contactsCard.growth
                          )} border-0`}
                        >
                          {formatGrowth(contactsCard.growth)}
                        </span>
                      </div>
                      <p className="fw-medium mb-1">{contactsCard.display_name}</p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      {visibleContacts.length > 0 && (
                        <div className="avatar-list-stacked avatar-group-sm">
                          {visibleContacts.map((contact) => (
                            <PersonAvatar
                              key={contact.contact_id}
                              name={contact.full_name}
                              url={contact.avatar_url}
                              sizeClass="border border-white"
                            />
                          ))}
                          {hiddenContactCount > 0 && (
                            <Link to={route.contactList} className="avatar bg-light text-dark fs-10 avatar-rounded">
                              +{hiddenContactCount}
                            </Link>
                          )}
                        </div>
                      )}
                      <p className="text-dark mb-0">From Last Period</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="row nd-section">
            <div className="col-xxl-4 col-xl-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title={`Top ${names.deals}`} />
                  </div>
                  {!loaded ? (
                    <TopDealsSkeleton />
                  ) : topDeals.length ? (
                    topDeals.map((deal) => (
                      <div
                        key={deal.deal_id}
                        className="d-flex align-items-sm-center justify-content-between gap-2 flex-sm-row flex-column mb-3"
                      >
                        <div className="d-flex align-items-center overflow-hidden">
                          <CompanyLogo name={deal.company_name || deal.deal_name} url={deal.company_logo_url} />
                          <div className="ms-2 flex-fill overflow-hidden">
                            <h6 className="fw-medium text-truncate mb-1 fs-14" title={deal.deal_name}>
                              <Link to={`/deals/${deal.deal_id}`}>{deal.deal_name}</Link>
                            </h6>
                            <p className="fs-13 mb-0 text-truncate">
                              {[deal.company_name, deal.country].filter(Boolean).join(" · ")}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm-end mb-0">
                          <p className="fw-semibold mb-0 text-dark">{formatCurrency(deal.deal_amount)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <EmptyState message={`No ${names.deals} in this period`} />
                  )}
                  <Link
                    className="btn btn-sm btn-light d-flex align-items-center justify-content-center mt-auto"
                    to={route.deals}
                  >
                    View All<i className="ti ti-chevron-right ms-1" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-xl-6 d-flex flex-column">
              <div className="card flex-fill">
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title={`${names.deal} Statistics by Amount`} />
                    <Link to={route.deals} className="btn btn-sm btn-icon btn-outline-light border">
                      <i className="ti ti-arrow-right" />
                    </Link>
                  </div>
                  {!loaded ? (
                    <PipelineSkeleton />
                  ) : hasPipelineData ? (
                    <>
                      <div className="row g-3 mb-3">
                        {pipelineStages.map((stage) => (
                          <div className={pipelineColumnClass} key={stage.key}>
                            <div>
                              <p className="mb-1">{stage.display_name}</p>
                              <p className="text-dark fw-bold mb-1">{formatCurrency(stage.revenue)}</p>
                              <p className="mb-0">{formatNumber(stage.count)} {names.deals}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="nd-stat-chart">
                        <div className="nd-stat-chart-inner">
                          <Chart options={pipelineChartOptions} series={pipelineSeries} type="bar" height="100%" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <EmptyState message={`No ${names.deal} data for this period`} />
                  )}
                </div>
              </div>
            </div>

            <div className="col-xxl-4 col-xl-6 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <SectionTitle title={`${names.deals} Overview by Status`} />
                    <Link to={route.deals} className="btn btn-sm btn-icon btn-outline-light border">
                      <i className="ti ti-arrow-right" />
                    </Link>
                  </div>

                  {!loaded ? (
                    <DealsOverviewSkeleton />
                  ) : (
                    <>
                      <div className="progress-stacked deals-progress gap-1 mb-3">
                        {dealSegmentTotal > 0 ? (
                          dealSegments.map((segment) => {
                            const width = ((dealsOverview?.[segment.key] ?? 0) / dealSegmentTotal) * 100;
                            return width > 0 ? (
                              <div
                                key={segment.key}
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${width}%`, backgroundColor: segment.color }}
                                aria-valuenow={width}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            ) : null;
                          })
                        ) : (
                          <div className="progress-bar nd-progress-empty" role="progressbar" style={{ width: "100%" }} />
                        )}
                      </div>

                      <div className="mb-4">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
                          <h4 className="mb-0">{formatNumber(dealsOverview?.total_deals ?? 0)}</h4>
                          <span
                            className={`d-inline-flex align-items-center badge rounded-pill ${growthBadgeClass(
                              dealsGrowth
                            )} border-0`}
                          >
                            {formatGrowth(dealsGrowth)}
                          </span>
                          <p className="mb-0">compared to last period</p>
                        </div>
                        {dealSegments.map((segment) => (
                          <div key={segment.key} className="p-2 d-flex align-items-center justify-content-between border-bottom">
                            <p className="text-dark d-flex align-items-center mb-0">
                              <i className="ti ti-circle-filled fs-8 me-1" style={{ color: segment.color }} />
                              {segment.label} {names.deals}
                            </p>
                            <p className="text-dark mb-0">{formatNumber(dealsOverview?.[segment.key] ?? 0)} {names.deals}</p>
                          </div>
                        ))}
                        <div className="p-2 d-flex align-items-center justify-content-between">
                          <p className="text-dark d-flex align-items-center mb-0">
                            <i className="ti ti-circle-filled fs-8 me-1 nd-muted-dot" />
                            {names.pipelines}
                          </p>
                          <p className="text-dark mb-0">
                            {formatNumber(dealsOverview?.pipelines ?? 0)} {names.pipelines}
                          </p>
                        </div>
                      </div>

                      <div
                        className="p-3 border rounded d-flex align-items-center justify-content-between"
                        style={{ backgroundColor: "#F7F8F9" }}
                      >
                        <div>
                          <p className="mb-1">{names.deals} Won</p>
                          <h4 className="mb-0">{formatNumber(dealsOverview?.won_deals ?? 0)}</h4>
                        </div>
                        <span className="avatar avatar-lg rounded-circle nd-won-icon">
                          <i className="ti ti-trophy fs-24" />
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row nd-section">
            <div className="col-md-12 d-flex">
              <div className="card flex-fill">
                <div className="card-body">
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                    <SectionTitle title={`Recent ${names.deals}`} />
                    <Link className="btn btn-sm btn-light d-inline-flex align-items-center" to={route.deals}>
                      View All<i className="ti ti-chevron-right ms-1" />
                    </Link>
                  </div>
                  <div className="table-responsive custom-table">
                    <table className="table table-bordered table-nowrap mb-0">
                      <thead className="table-white">
                        <tr>
                          <th>{names.deal} Name</th>
                          <th>Stage</th>
                          <th className="text-end">{names.deal} Value</th>
                          <th>Tags</th>
                          <th>Owner</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {!loaded ? (
                          <RecentDealsSkeletonRows />
                        ) : recentDeals.length ? (
                          recentDeals.map((deal) => (
                            <tr key={deal.deal_id}>
                              <td>
                                <TruncatedLink
                                  to={`/deals/${deal.deal_id}`}
                                  text={deal.deal_name}
                                  className="fw-medium text-dark"
                                />
                              </td>
                              <td>{deal.stage_name || "-"}</td>
                              <td className="text-end">{formatCurrency(deal.deal_value)}</td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {deal.tags.length
                                    ? deal.tags.map((tag) => (
                                      <span key={tag} className={`badge nd-tag ${tagClassName(tag)}`}>
                                        {tag}
                                      </span>
                                    ))
                                    : "-"}
                                </div>
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <PersonAvatar
                                    name={deal.owner_name || "Unassigned"}
                                    url={deal.owner_avatar_url}
                                    sizeClass="avatar-xs me-2"
                                  />
                                  <span className="text-dark">{deal.owner_name || "Unassigned"}</span>
                                </div>
                              </td>
                              <td>
                                {deal.status ? (
                                  <span className={`badge rounded-pill ${statusClassName(deal.status)}`}>
                                    {deal.status}
                                  </span>
                                ) : (
                                  "-"
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6}>
                              <EmptyState message={`No recent ${names.deals}`} compact />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewDashboard;
